import React from 'react';
import {
  Upload,
  X,
  Info,
  FileText,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash,
} from 'lucide-react';

export default function UploadStep({
  uploadedFiles,
  additionalText,
  dragActive,
  processingFiles,
  processingError,
  onFileUpload,
  onFileRemove,
  onClearAll,
  onAdditionalTextChange,
  onDrag,
  onDrop,
  onNext,
}) {
  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      onFileUpload(e.target.files);
      e.target.value = '';
    }
  };

  const handleClearAll = () => {
    onClearAll();
  };

  return (
    <div className="bg-[#1E1F22] h-full p-4 rounded-md w-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <Upload className="w-5 h-5 mr-2 text-white" />
          <h1 className="text-lg font-semibold text-white">
            Upload Training Materials
          </h1>
        </div>
        <button
          onClick={handleClearAll}
          className="text-xs text-gray-400 hover:text-white border border-gray-600 px-3 py-1.5 rounded transition-colors"
        >
          Clear All
        </button>
      </div>

      <p className="text-gray-500 text-xs mb-4">
        Add your storyboard drafts here along with any additional notes.
      </p>

      <div className="flex-1 min-h-0 overflow-auto pr-1">
        <div className="grid grid-cols-3 gap-6 min-w-[680px] h-full">
          <div className="col-span-2 flex flex-col gap-6">
            <div className="bg-[#292A2E] rounded-md p-4">
              <div className="flex items-center mb-3">
                <div className="flex items-center text-red-500 text-xs font-medium">
                  <Upload className="w-4 h-4 mr-1.5" />
                  Upload Files
                </div>
                <Info className="w-3.5 h-3.5 ml-2 text-gray-500" />
              </div>

              <div
                className={`border-2 flex justify-center items-center border-dashed rounded-lg p-10 text-center transition-colors ${
                  dragActive ? 'border-red-500 bg-red-500/5' : 'border-gray-700'
                }`}
                onDragEnter={onDrag}
                onDragLeave={onDrag}
                onDragOver={onDrag}
                onDrop={onDrop}
              >
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept=".pdf,.docx,.pptx,.txt"
                  onChange={handleChange}
                  className="hidden"
                />
                <label htmlFor="file-upload" className="cursor-pointer block">
                  <div className="text-gray-400 text-sm">
                    Drag and drop files here or{' '}
                    <span className="text-red-500 underline">
                      click to browse
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Supported formats: PDF, DOCX, PPTX, TXT
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Maximum file size: 25MB
                  </div>
                </label>
              </div>
            </div>

            <div className="bg-[#292A2E] rounded-md p-4 flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-medium text-white">
                  Uploaded Files
                </h3>
                {processingFiles && (
                  <div className="flex items-center text-xs text-yellow-400">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Processing...
                  </div>
                )}
              </div>
              {uploadedFiles.length === 0 ? (
                <div className="bg-[#292A2E] rounded-lg border border-gray-800 p-10 flex flex-col items-center justify-center">
                  <div className="w-16 h-20 mb-3 opacity-20">
                    <FileText className="w-full h-full text-gray-600" />
                  </div>
                  <p className="text-gray-600 text-xs">No files uploaded yet</p>
                </div>
              ) : (
                <div className="bg-[#292A2E] rounded-lg border border-gray-800 overflow-hidden max-h-[220px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#292A2E]">
                      <tr className="border-b border-gray-800">
                        <th className="text-gray-500 text-center font-medium px-4 py-2.5">
                          Name
                        </th>
                        <th className="text-gray-500 text-center font-medium px-4 py-2.5">
                          Size
                        </th>
                        <th className="text-gray-500 text-center font-medium px-4 py-2.5">
                          Status
                        </th>
                        <th className="text-gray-500 text-center font-medium px-4 py-2.5">
                          Advanced Parsing
                        </th>
                        <th className="text-gray-500 text-center font-medium px-4 py-2.5 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadedFiles.map((file, index) => (
                        <tr
                          key={file.id}
                          className={`${
                            index !== uploadedFiles.length - 1
                              ? 'border-b border-gray-800'
                              : ''
                          } hover:bg-zinc-800/50`}
                        >
                          <td className="px-4 py-3 text-gray-300 text-center">
                            {file.name}
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-center">
                            {file.size
                              ? (file.size / 1024).toFixed(2) + ' KB'
                              : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {file.status === 'success' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/30 text-green-400 border border-green-700">
                                Completed
                              </span>
                            ) : file.status === 'error' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-900/30 text-red-400 border border-red-700">
                                Failed
                              </span>
                            ) : file.status === 'processing' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-700">
                                Processing
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-800 text-gray-400 border border-gray-700">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 flex justify-center items-center">
                            {file.status === 'success' && (
                              <CheckCircle2 className="w-4 h-4 text-green-400" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => onFileRemove(file.id)}
                              className="text-gray-500 hover:text-white transition-colors"
                              disabled={processingFiles}
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {processingError && (
                <div className="mt-4 bg-red-900/20 border border-red-800 rounded-lg p-3 text-xs text-red-400">
                  {processingError}
                </div>
              )}
            </div>
          </div>

          <div className="col-span-1 bg-[#292A2E] rounded-md p-4 flex flex-col">
            <div className="flex items-center mb-3">
              <div className="text-xs font-medium text-white">
                Additional Text Input
              </div>
              <Info className="w-3.5 h-3.5 ml-2 text-gray-500" />
            </div>

            <textarea
              value={additionalText}
              onChange={(e) => onAdditionalTextChange(e.target.value)}
              className="w-full flex-1 min-h-[200px] bg-[#404040] border-gray-800 text-gray-300 rounded-lg p-4 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
              placeholder="Assembly Line Maintenance Training is my adventure..."
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-4 pt-2 border-t border-gray-800">
        <button
          onClick={onNext}
          disabled={
            processingFiles ||
            (uploadedFiles.length === 0 && !additionalText.trim())
          }
          className="bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white px-5 py-2 rounded text-xs font-medium flex items-center transition-colors"
        >
          {processingFiles ? (
            <>
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              Process Input
              <ChevronRight className="w-4 h-4 ml-1" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
